import { ModalStore } from '../../state/ModalStore'
import HomepageBookCallLeadModal from '../Modals/HomepageBookCallLeadModal'

function XIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    )
}

function CheckCircleIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    )
}

const COMPARISONS = [
    {
        bad: {
            title: 'Basic radius lists',
            desc: 'Broad ZIPs or carrier routes with little strategy',
        },
        good: {
            title: 'Smarter homeowner targeting',
            desc: 'Property, homeowner, geography, home-age, value, storm, and service-area filters',
        },
    },
    {
        bad: {
            title: 'Slow campaign launches',
            desc: 'Missed timing when storm, season, or demand shifts',
        },
        good: {
            title: 'Fast production options',
            desc: 'In-house print and mail with 2-business-day production available',
        },
    },
    {
        bad: {
            title: 'Generic creative',
            desc: 'Same postcard message regardless of audience or job type',
        },
        good: {
            title: 'Roofing-specific campaigns',
            desc: 'Messaging built around inspections, replacement, storm damage, repairs, and neighborhoods',
        },
    },
    {
        bad: {
            title: 'No response visibility',
            desc: 'Hard to know which campaign created calls',
        },
        good: {
            title: 'Trackable performance',
            desc: 'Call tracking, QR codes, and campaign-level reporting',
        },
    },
    {
        bad: {
            title: 'One-off postcard orders',
            desc: 'No cadence, testing plan, or next step',
        },
        good: {
            title: 'Repeatable growth program',
            desc: 'Audience, offer, format, cadence, and follow-up aligned to your market',
        },
    },
]

export default function WhyOLM({ onBookCall }) {
    const addModal = ModalStore((state) => state.add)
    const handleBookCall = () => {
        if (onBookCall) {
            onBookCall()
            return
        }

        addModal(<HomepageBookCallLeadModal />)
    }

    return (
        <section className="bg-[#3F454F]">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 lg:py-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
                <div className="flex flex-col gap-4">
                    <span className="text-[#FDB515] text-xs uppercase tracking-widest font-semibold">
                        Why OLM
                    </span>
                    <h2
                        className="text-white tracking-tight text-3xl md:text-[44px] font-bold"
                        style={{ lineHeight: 1.05 }}>
                        Built for roofers who need more than just good pricing.
                    </h2>
                    <p
                        className="text-white/70 text-lg"
                        style={{ lineHeight: '28px' }}>
                        Roofing demand is local, timing-sensitive, and won by
                        the company homeowners remember first. We help you get
                        in front of the right doors before competitors own the
                        neighborhood.
                    </p>
                    <p
                        className="text-white/70 text-lg"
                        style={{ lineHeight: '28px' }}>
                        From older roofs and storm-hit streets to neighborhoods
                        around active jobsites, OLM turns direct mail into a
                        focused growth channel, not another batch of postcards
                        sent on hope.
                    </p>
                    <div className="mt-2">
                        <button
                            type="button"
                            onClick={handleBookCall}
                            className="inline-flex w-fit cursor-pointer px-6 py-3 text-base rounded-full text-white font-semibold transition-shadow"
                            style={{
                                background:
                                    'linear-gradient(to right, #ED5C2F 20%, #F28230 70%, #FAB331 110%)',
                                boxShadow:
                                    '0 0 9px rgba(237,92,47,0.28), 0 1px 4px rgba(237,92,47,0.22)',
                            }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.boxShadow =
                                    '0 0 13px rgba(237,92,47,0.42), 0 2px 7px rgba(237,92,47,0.3)')
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.style.boxShadow =
                                    '0 0 9px rgba(237,92,47,0.28), 0 1px 4px rgba(237,92,47,0.22)')
                            }>
                            Book a call &rarr;
                        </button>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    {/* Column headers - desktop only */}
                    <div className="hidden lg:grid grid-cols-2 gap-4 px-5">
                        <span className="text-xs uppercase tracking-widest text-white/40 font-semibold">
                            Typical mail vendor
                        </span>
                        <span className="text-xs uppercase tracking-widest text-[#FDB515] font-semibold">
                            OLM
                        </span>
                    </div>

                    {COMPARISONS.map((row, i) => (
                        <div
                            key={i}
                            className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-3.5 bg-white/5 rounded-xl border border-white/10 transition-all duration-200 hover:bg-white/[0.08] hover:border-white/20">
                            {/* Bad */}
                            <div className="flex flex-row items-start gap-3">
                                <div className="w-5 h-5 shrink-0 rounded-full bg-secondary/20 flex items-center justify-center mt-0.5">
                                    <XIcon className="w-3 h-3 text-secondary" />
                                </div>
                                <div className="text-sm text-white/60">
                                    <strong className="text-white/60">
                                        {row.bad.title}
                                    </strong>
                                    <br />
                                    {row.bad.desc}
                                </div>
                            </div>
                            {/* Good */}
                            <div className="flex flex-row items-start gap-3 pt-3 mt-1 border-t border-white/10 lg:pt-0 lg:mt-0 lg:border-t-0">
                                <div className="w-5 h-5 shrink-0 rounded-full bg-emerald-500/20 flex items-center justify-center mt-0.5">
                                    <CheckCircleIcon className="w-3 h-3 text-emerald-500" />
                                </div>
                                <div className="text-sm text-white">
                                    <strong>{row.good.title}</strong>
                                    <br />
                                    <span className="text-white/80">
                                        {row.good.desc}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}
